import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControlLabel,
    Switch,
    Alert,
    Box,
    CircularProgress,
    Typography
} from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { updateMissionTemplate, checkTemplateNameUnique } from '../network/apis/missionTemplate.api';
import { useOrganization } from '../context/OrganizationContext';

/**
 * Edit Template Dialog Component
 * Allows editing an existing operation template
 *
 * Props:
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close dialog callback
 * @param {Object} template - Template to edit
 * @param {function} onSuccess - Success callback
 */
const EditTemplateDialog = ({ open, onClose, template, onSuccess }) => {
    const { getAccessTokenSilently, user } = useAuth0();
    const { currentOrganization, userRole } = useOrganization();

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        isShared: false
    });

    // UI state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [nameError, setNameError] = useState(null);
    const [checkingName, setCheckingName] = useState(false);

    // Check user role
    const isOwnerOrManager = userRole && ['owner', 'admin', 'manager'].includes(userRole.toLowerCase());

    // Check if user can only edit their own templates
    const isCreator = template?.createdBy?.userId === user?.sub;
    const canEdit = isOwnerOrManager || isCreator;

    // Initialize form when dialog opens or template changes
    useEffect(() => {
        if (open && template) {
            setFormData({
                name: template.name || '',
                description: template.description || '',
                isShared: template.isShared || false
            });
            setError(null);
            setNameError(null);
        }
    }, [open, template]);

    // Debounced name uniqueness check (excluding current template)
    useEffect(() => {
        const checkName = async () => {
            if (!formData.name.trim()) {
                setNameError(null);
                return;
            }

            // Skip check if name hasn't changed
            if (template && formData.name === template.name) {
                setNameError(null);
                return;
            }

            setCheckingName(true);
            try {
                const token = await getAccessTokenSilently();
                const isAvailable = await checkTemplateNameUnique(
                    formData.name,
                    token,
                    template?.id, // Exclude current template from check
                    currentOrganization?.organizationUuid || null
                );

                if (!isAvailable) {
                    setNameError('This template name is already in use. Please choose a different name.');
                } else {
                    setNameError(null);
                }
            } catch (err) {
            } finally {
                setCheckingName(false);
            }
        };

        const timer = setTimeout(checkName, 500);
        return () => clearTimeout(timer);
    }, [formData.name, currentOrganization, template]);

    const handleFormChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async () => {
        // Validation
        if (!formData.name.trim()) {
            setError('Template name is required');
            return;
        }

        if (nameError) {
            setError('Please fix the errors before submitting');
            return;
        }

        if (!template) {
            setError('No template data available');
            return;
        }

        if (!canEdit) {
            setError('You do not have permission to edit this template');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const token = await getAccessTokenSilently();

            const updates = {
                name: formData.name.trim(),
                description: formData.description.trim(),
                isShared: formData.isShared
            };

            await updateMissionTemplate(
                template.id,
                updates,
                token,
                currentOrganization?.organizationUuid || null
            );

            // Success
            if (onSuccess) {
                onSuccess();
            }
            onClose();
        } catch (err) {
            if (err.response?.data?.error) {
                setError(err.response.data.error);
            } else if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else {
                setError('Failed to update template. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Check if form has changes
    const hasChanges = template && (
        formData.name !== template.name ||
        formData.description !== (template.description || '') ||
        formData.isShared !== template.isShared
    );

    if (!template) {
        return null;
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Edit Operation Template
            </DialogTitle>

            <DialogContent>
                {/* Permission warning */}
                {!canEdit && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        You do not have permission to edit this template.
                    </Alert>
                )}

                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Template Info */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Template Information
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Created:</strong> {new Date(template.createdAt).toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Operation Type:</strong> {template.operationType === 'part_107' ? 'Part 107' : '44809'}
                        </Typography>
                        {template.duration && (
                            <Typography variant="body2" color="text.secondary">
                                <strong>Duration:</strong> {template.duration} minutes
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Form Fields */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        label="Template Name"
                        required
                        fullWidth
                        value={formData.name}
                        onChange={(e) => handleFormChange('name', e.target.value)}
                        placeholder="e.g., Downtown Building Inspection"
                        error={!!nameError}
                        helperText={nameError || 'Choose a unique, descriptive name for this template'}
                        disabled={!canEdit}
                        InputProps={{
                            endAdornment: checkingName && <CircularProgress size={20} />
                        }}
                    />

                    <TextField
                        label="Description (Optional)"
                        fullWidth
                        multiline
                        rows={3}
                        value={formData.description}
                        onChange={(e) => handleFormChange('description', e.target.value)}
                        placeholder="Describe when to use this template..."
                        helperText="Add details about the operation type, location, or special requirements"
                        disabled={!canEdit}
                    />

                    {/* Sharing Option - Only show if user is creator */}
                    {isCreator && (
                        <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isShared}
                                        onChange={(e) => handleFormChange('isShared', e.target.checked)}
                                        disabled={!canEdit}
                                    />
                                }
                                label="Share with organization"
                            />
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4 }}>
                                {formData.isShared
                                    ? 'All team members can view and use this template'
                                    : 'Only you can see and use this template'}
                            </Typography>

                            {isOwnerOrManager && (
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    As {userRole}, you can always see all templates created by your team
                                </Alert>
                            )}
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || !formData.name.trim() || !!nameError || checkingName || !hasChanges || !canEdit}
                    startIcon={loading && <CircularProgress size={16} />}
                >
                    {loading ? 'Updating...' : 'Update Template'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditTemplateDialog;
